<template>
  <div class="faq-custom-section">
    <div class="text-subtitle-1 font-weight-bold mb-1">自訂常見問題</div>
    <div class="text-body-2 grey--text text--darken-1 mb-4">
      <template v-if="canEdit">
        最多可新增 10 個問題，每題最多 10 組回覆，拖拉左側把手可調整順序。回覆字數上限 225 字
      </template>
      <template v-else>
        自訂問題設定，點擊可展開查看回覆。
      </template>
    </div>

    <!-- 空狀態 -->
    <div v-if="items.length === 0 && !canEdit" class="text-body-2 grey--text">
      尚未新增自訂問答
    </div>

    <draggable
      :value="items"
      @input="updateItems"
      :disabled="!canEdit"
      handle=".question-drag"
      animation="200"
    >
      <transition-group type="transition" name="flip-list">
        <div
          v-for="(custom, cIdx) in items"
          :key="custom.id || `custom-${cIdx}`"
          class="faq-card mb-3 rounded-lg"
        >
          <!-- 問題標題行 -->
          <div
            class="faq-question-row d-flex align-center cursor-pointer"
            :class="canEdit ? 'faq-question-row--edit px-4' : 'pa-3'"
            @click="toggleExpand(cIdx)"
          >
            <v-icon
              v-if="canEdit"
              class="question-drag mr-2"
              color="grey"
              @click.stop
            >mdi-drag</v-icon>

            <template v-if="canEdit">
              <v-text-field
                :value="custom.question"
                @input="val => updateField(cIdx, 'question', val)"
                placeholder="請輸入問題標題"
                dense
                hide-details
                outlined
                class="flex-grow-1 faq-question-input"
                :error="!custom.question || !custom.question.trim()"
                @click.stop
              />
              <v-btn icon small color="red" class="ml-1" @click.stop="removeQuestion(cIdx)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span v-else class="flex-grow-1 text-body-2 font-weight-medium">{{ custom.question }}</span>

            <v-icon class="ml-1">
              {{ expanded[cIdx] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
          </div>

          <!-- 展開內容 -->
          <div v-if="expanded[cIdx]" class="faq-options-area" :class="canEdit ? 'faq-options-area--edit pa-4 pl-7' : 'px-3 pb-3'">
            <!-- View 模式 -->
            <template v-if="!canEdit">
              <ul class="pl-4">
                <li
                  v-for="(opt, oIdx) in custom.options"
                  :key="oIdx"
                  class="text-body-2 mb-1"
                >{{ opt.option_text }}</li>
              </ul>
              <div v-if="!custom.options || custom.options.length === 0" class="text-body-2 grey--text">
                尚無回覆
              </div>
            </template>

            <!-- Edit 模式 -->
            <template v-else>
              <draggable
                :value="custom.options"
                @input="val => updateOptions(cIdx, val)"
                handle=".option-drag"
                animation="200"
              >
                <transition-group type="transition" name="flip-list">
                  <div
                    v-for="(opt, oIdx) in custom.options"
                    :key="opt.id || `opt-${oIdx}`"
                    class="d-flex align-center mb-2"
                  >
                    <v-icon class="option-drag mr-2" color="grey">mdi-drag</v-icon>
                    <v-text-field
                      :value="opt.option_text"
                      @input="val => updateOptionField(cIdx, oIdx, 'option_text', val)"
                      placeholder="輸入回覆內容..."
                      dense
                      hide-details
                      outlined
                      :maxlength="225"
                      counter="225"
                      class="flex-grow-1"
                      :error="!opt.option_text || !opt.option_text.trim()"
                    />
                    <v-btn icon small color="grey" class="ml-1" @click="removeOption(cIdx, oIdx)">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </div>
                </transition-group>
              </draggable>
              <v-btn
                v-if="custom.options.length < 10"
                text
                small
                color="primary"
                class="mt-1"
                @click="addOption(cIdx)"
              >
                <v-icon left small>mdi-plus</v-icon>
                新增自訂回覆
              </v-btn>
            </template>
          </div>
        </div>
      </transition-group>
    </draggable>

    <!-- 新增問題按鈕 -->
    <v-btn
      v-if="canEdit && items.length < 10"
      text
      color="primary"
      class="mt-2"
      @click="addQuestion"
    >
      <v-icon left small>mdi-plus</v-icon>
      新增問題
    </v-btn>
  </div>
</template>

<script>
import draggable from 'vuedraggable'

export default {
  name: 'FaqCustomSection',
  components: { draggable },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    canEdit: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      expanded: {}
    }
  },
  methods: {
    toggleExpand(idx) {
      this.$set(this.expanded, idx, !this.expanded[idx])
    },
    updateItems(val) {
      this.$emit('update:items', val)
    },
    updateField(cIdx, field, val) {
      const newItems = JSON.parse(JSON.stringify(this.items))
      newItems[cIdx][field] = val
      this.$emit('update:items', newItems)
    },
    updateOptions(cIdx, newOptions) {
      const newItems = JSON.parse(JSON.stringify(this.items))
      newItems[cIdx].options = newOptions
      this.$emit('update:items', newItems)
    },
    updateOptionField(cIdx, oIdx, field, val) {
      const newItems = JSON.parse(JSON.stringify(this.items))
      newItems[cIdx].options[oIdx][field] = val
      this.$emit('update:items', newItems)
    },
    addOption(cIdx) {
      const newItems = JSON.parse(JSON.stringify(this.items))
      newItems[cIdx].options.push({
        option_text: ''
      })
      this.$emit('update:items', newItems)
    },
    removeOption(cIdx, oIdx) {
      const newItems = JSON.parse(JSON.stringify(this.items))
      newItems[cIdx].options.splice(oIdx, 1)
      this.$emit('update:items', newItems)
    },
    addQuestion() {
      const newItems = JSON.parse(JSON.stringify(this.items))
      newItems.push({
        question: '',
        is_enabled: 1,
        options: []
      })
      this.$emit('update:items', newItems)
    },
    removeQuestion(cIdx) {
      const newItems = JSON.parse(JSON.stringify(this.items))
      newItems.splice(cIdx, 1)
      this.$emit('update:items', newItems)
    }
  }
}
</script>

<style scoped lang="scss">
.faq-card {
  border: 1px solid #E5E5E5;
  background: #F9F9F9;
  border-radius: 6px;
  overflow: hidden;
}

.faq-question-row--edit {
  background: #F0F0F0;
  border-bottom: 1px solid #E5E5E5;
  min-height: 44px;
}

.faq-question-input ::v-deep .v-input__slot {
  font-weight: 600;
}

.faq-options-area--edit {
  background: #F9F9F9;
}

.cursor-pointer {
  cursor: pointer;
}

.question-drag,
.option-drag {
  cursor: grab;
  &:active {
    cursor: grabbing;
  }
}

.flip-list-move {
  transition: transform 0.3s;
}
</style>
